'use client';

import type { AnchorHTMLAttributes, MouseEvent } from 'react';

import { useBoundUrlSource } from '../../client/security/useBoundUrls.ts';
import { inAppBrowserUrl, openWebPage, type InAppBrowserOptions } from '../../shared/browser/inAppBrowser.ts';
import { boundLinkLabel, boundLinkText } from './boundLinkLabel.tsx';

type AnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel' | 'onAuxClick'>;

export interface InAppLinkProps extends AnchorProps, Pick<InAppBrowserOptions, 'parentId' | 'highlightSentences' | 'canEmbed'> {
  /** The page to open. An unsafe or unbound address renders the content without a link. */
  href: string | null | undefined;
  /** The viewer window's title; defaults to the link's `title`. */
  viewerTitle?: string;
  /**
   * The content is app copy (translated strings, icons), never data or
   * model text, so a search-result or outside-text link keeps it. Without
   * it, that link shows its host instead. Generic copy names the
   * destination ("Open on {host}", linkLabelHost) so the host shows once.
   */
  appCopy?: boolean;
}

/**
 * A link to a web page that opens in Smart Manager's own browser (the
 * WebViewer window). Only a bound address (boundUrlSet.ts) is a link: it
 * stays a real anchor, so copy-link, focus and screen readers behave as for
 * any link, and the click (or middle-click) opens it with that click as the
 * user's gesture. On a page with no workspace for a viewer (a public page)
 * the same click opens a new browser tab. An unbound address is plain
 * text: no tap can open it. A search-result or outside-text link is named
 * by code (its host, boundLinkLabel.tsx), not by its content.
 */
export function InAppLink({
  href,
  viewerTitle,
  parentId,
  highlightSentences,
  canEmbed,
  appCopy = false,
  onClick,
  children,
  ...anchorProps
}: InAppLinkProps) {
  const safeHref = inAppBrowserUrl(href);
  const source = useBoundUrlSource(safeHref);
  const codeLabel = boundLinkLabel(safeHref, source);
  if (!safeHref || !source) {
    return <span className={anchorProps.className} data-nv-unbound-link={safeHref ? 'true' : undefined}>{children}</span>;
  }

  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    // The anchor never navigates by itself: every open goes through the rule.
    event.preventDefault();
    openWebPage(safeHref, {
      // A code-labelled page's viewer is named by its host, not by the caller.
      title: codeLabel ? undefined : viewerTitle ?? anchorProps.title,
      parentId,
      highlightSentences,
      canEmbed,
      gesture: event,
    });
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    open(event);
  };

  // A middle-click would open a new tab straight from the href.
  const handleAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 1) open(event);
  };

  const text = boundLinkText(safeHref, codeLabel, appCopy, children, anchorProps.title, anchorProps['aria-label']);
  return (
    <a
      {...anchorProps}
      title={text.title}
      aria-label={text.ariaLabel}
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      onAuxClick={handleAuxClick}
    >
      {text.content}
    </a>
  );
}

export default InAppLink;
